<template>
  <div class="text-viewer-container">
    <div 
      class="text-content"
      data-test="text-content"
      @mouseup="handleTextSelection"
      @touchstart.passive="handleTouchStart"
      @touchend.passive="handleTouchSelection"
    >
      <p
        v-for="(section, index) in documentSections"
        :key="index"
        class="text-section"
        v-html="highlightSection(section)"
      />
      <p
        v-if="documentSections.length === 0"
        class="loading-text"
      >
        Receiving content...
      </p>
    </div>
  </div>
</template>

<script>
/**
 * TextViewer - Displays streamed text content with vocabulary selection
 * Allows users to select text for vocabulary lookup
 */
export default {
  name: 'TextViewer',
  props: {
    documentSections: {
      type: Array,
      default: () => []
    },
    activeSentenceText: {
      type: String,
      default: null
    }
  },
  emits: ['text-selected','textSelectedOnTextReader', 'document-loaded'],
  data() {
    return {
      touchStartX: 0,
      touchStartY: 0,
    };
  },
  watch: {
    documentSections: {
      handler(newSections) {
        if (newSections && newSections.length > 0) {
          this.$emit('document-loaded');
        }
      },
      immediate: true
    }
  },
  methods: {
    /**
     * Returns HTML string for a section with the active sentence wrapped in <mark>.
     * All user-facing text is HTML-escaped before injection to prevent XSS.
     */
    highlightSection(section) {
      const escaped = section
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      if (!this.activeSentenceText) return escaped;
      const escapedSentence = this.activeSentenceText
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return escaped.replace(
        new RegExp(escapedSentence, 'g'),
        '<mark class="voice-highlight">$&</mark>'
      );
    },

    handleTextSelection() {
      const selection = window.getSelection();
      const selectedText = selection.toString().trim();

      if (selectedText.length > 0) {
        this.$emit('text-selected', {
          text: selectedText,
          pageNumber: 1
        });

        // Clear selection after emission
        selection.removeAllRanges();
      }
    },

    handleTouchStart(event) {
      const touch = event.touches[0];
      if (touch) {
        this.touchStartX = touch.clientX;
        this.touchStartY = touch.clientY;
      }
      // Clear any leftover selection so touchend only sees selections made during this touch
      window.getSelection()?.removeAllRanges();
    },

    async handleTouchSelection(event) {
      // Ignore if the finger moved more than 8px — it was a scroll, not a tap
      const touch = event.changedTouches[0];
      if (touch) {
        const dx = Math.abs(touch.clientX - this.touchStartX);
        const dy = Math.abs(touch.clientY - this.touchStartY);
        if (dx > 8 || dy > 8) return;
      }

      // Delay slightly so the browser finalises the selection state
      setTimeout(async () => {
        // Emit the event to the parent component
        this.$emit('textSelectedOnTextReader', event);
      }, 50);
    }
  }
};
</script>

<style scoped>
.text-viewer-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
}

.text-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  font-size: 16px;
  line-height: 1.8;
  color: #333;
  word-wrap: break-word;
  user-select: text;
  background-color: white;
}

.text-section {
  margin-bottom: 15px;
  text-align: justify;
  cursor: text;
}

.text-section:last-child {
  margin-bottom: 0;
}

.loading-text {
  color: #999;
  font-style: italic;
  text-align: center;
  padding: 40px 20px;
}

/* Scrollbar styling */
.text-content::-webkit-scrollbar {
  width: 8px;
}

.text-content::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.text-content::-webkit-scrollbar-thumb {
  background: #667eea;
  border-radius: 4px;
}

.text-content::-webkit-scrollbar-thumb:hover {
  background: #764ba2;
}

/* Text selection styling */
.text-content ::selection {
  background-color: #667eea;
  color: white;
}

.text-content ::-moz-selection {
  background-color: #667eea;
  color: white;
}

.voice-highlight {
  background-color: #ffe066;
  color: inherit;
  border-radius: 2px;
  padding: 0 1px;
}
</style>
